import { CreditBalanceBySourceInner } from 'ldt-billing-service-api';

// A credit balance for one credit type, ready for display. For types reported
// by the balance endpoint this is the live remaining amount; for the monthly
// row it is the recurring allowance configured in org settings (see
// foldCreditBySource).
export interface CreditTypeBalance {
  label: string;
  remaining: number;
}

const MONTHLY_CREDITS_LABEL = 'Monthly credits';

// Customer-facing labels for the credit sources billing reports in the live
// balance breakdown. Unknown sources fall back to the raw name so a new
// backend source degrades to something readable rather than being dropped.
const CREDIT_SOURCE_LABELS: { [source: string]: string } = {
  free_tier: 'One-time credits',
  freeTier: 'One-time credits',
  monthly: MONTHLY_CREDITS_LABEL,
  monthly_credit: MONTHLY_CREDITS_LABEL,
  carryover: 'Rolled-over credits',
  addon: 'Purchased credits',
  manual: 'Granted credits',
};

// Fold billing's per-credit-row by_source entries into per-type totals with
// customer-facing labels. The one-time/purchased rows keep the endpoint's
// order and the monthly row always closes the list, showing `monthlyCredit`:
// the recurring allowance from the org's settings (GET /orgs/{orgId}), which
// is authoritative because the balance endpoint never reports the monthly
// allowance among its credit rows (a row claiming to be monthly is ignored).
// Closing the list keeps the monthly row in place when a display seeds the
// list with monthly alone: the fetched rows land above it, where the
// loading placeholder sits.
export function foldCreditBySource(
  bySource: CreditBalanceBySourceInner[],
  monthlyCredit: number
): CreditTypeBalance[] {
  const totals = new Map<string, number>();
  for (const row of bySource) {
    const label =
      CREDIT_SOURCE_LABELS[row.source] ||
      row.source.charAt(0).toUpperCase() + row.source.slice(1) + ' credits';
    if (label !== MONTHLY_CREDITS_LABEL) {
      totals.set(label, (totals.get(label) || 0) + row.remaining);
    }
  }
  totals.set(MONTHLY_CREDITS_LABEL, monthlyCredit);
  return Array.from(totals, ([label, remaining]) => ({ label, remaining }));
}
